import { create } from "zustand";
import {
  BOARD_SIZE,
  SIM_VERSION,
  emptyLayout,
  idx,
  simulate,
  validateLayout,
  type BattleResult,
  type Layout,
  type Tile,
  type TileKind,
  type TowerKind,
} from "@empire/core-sim";
import type { IntelResponse, WireFogView } from "@empire/shared";
import { fetchIntel } from "../api/attack.js";
import { toPlan, usePlanStore } from "./plan.js";
import { useSessionStore } from "./session.js";

/**
 * What you know about a target, and testing a plan against it (S3-2, S3-3).
 *
 * The dry run here is the same `simulate` the server runs — but on a board that
 * is a GUESS, not the truth. Everything in this file exists to keep that
 * distinction visible, because a confident answer from incomplete information
 * is worse than no answer.
 */

export type DryRunStatus = "idle" | "running" | "done" | "impossible";

interface IntelState {
  villageId: string | null;
  intel: IntelResponse | null;
  loading: boolean;
  error: string | null;

  status: DryRunStatus;
  result: BattleResult | null;
  /** Why the guessed board could not be simulated at all. */
  blocked: string | null;

  load: (villageId: string) => Promise<void>;
  dryRun: () => void;
  clearRun: () => void;
}

export const useIntelStore = create<IntelState>((set, get) => ({
  villageId: null,
  intel: null,
  loading: false,
  error: null,
  status: "idle",
  result: null,
  blocked: null,

  load: async (villageId) => {
    const token = await useSessionStore.getState().token();
    if (!token) return;
    // Changing target throws away the old answer rather than leaving it on
    // screen looking like it is about the new one.
    set({ loading: true, error: null, villageId, intel: null, status: "idle", result: null });
    try {
      const intel = await fetchIntel(token, villageId);
      set({ intel, loading: false });
    } catch {
      set({ loading: false, error: "Couldn't load what you know about this village." });
    }
  },

  dryRun: () => {
    const { intel } = get();
    const plan = toPlan(usePlanStore.getState().waves);
    if (!intel?.view || plan.waves.length === 0) return;

    const guess = guessedLayout(intel.view);
    const legal = validateLayout(guess);
    if (!legal.ok) {
      // The most common case by far, and it is not an error: you have not seen
      // enough of the road for it to join up yet. Saying so is more useful than
      // a result, because it names exactly what another attack would buy.
      set({
        status: "impossible",
        result: null,
        blocked:
          intel.view.unknownCount > 0
            ? "You have not seen enough of the road to guess where it goes. Attack again to find more of it."
            : legal.error.message,
      });
      return;
    }

    set({ status: "running", blocked: null });
    const result = simulate({ simVersion: SIM_VERSION, layout: guess, plan });
    set({ status: "done", result });
  },

  clearRun: () => set({ status: "idle", result: null, blocked: null }),
}));

/**
 * The board you would be attacking IF what you know is all there is.
 *
 * Fog becomes empty ground. That is the only honest assumption available — a
 * tile you have never seen could be anything — but it is also why the answer is
 * labelled partial everywhere it appears. The real maze can only be worse than
 * this guess, never better: every hidden tower is a tower this run did not have
 * to walk past.
 */
export function guessedLayout(view: WireFogView): Layout {
  const base = emptyLayout();
  const tiles: Tile[] = base.tiles.map((t) => ({ kind: t.kind }));

  for (let i = 0; i < view.tiles.length && i < tiles.length; i++) {
    const seen = view.tiles[i];
    if (!seen) continue;
    tiles[i] = seen.tower
      ? {
          kind: seen.kind as TileKind,
          tower: { kind: seen.tower.kind as TowerKind, level: seen.tower.level as 1 | 2 | 3 },
        }
      : { kind: seen.kind as TileKind };
  }

  const [gx, gy] = view.gate;
  return {
    ...base,
    tiles,
    gate: { x: gx, y: gy },
    wallLevel: view.wallLevel,
    // A guessed board needs enough slots to hold the towers already on it, or
    // validateLayout would refuse a layout the defender is legally running.
    engineersGuildLevel: 20,
  };
}

/** How much of the defence is still unknown, as a share for a progress bar. */
export function knownShare(intel: IntelResponse | null): number {
  if (!intel?.view) return 0;
  const total = intel.view.unknownCount + countKnownDefence(intel.view);
  if (total === 0) return 1;
  return countKnownDefence(intel.view) / total;
}

function countKnownDefence(view: WireFogView): number {
  let known = 0;
  for (let i = 0; i < view.tiles.length; i++) {
    const tile = view.tiles[i];
    if (!tile) continue;
    if (tile.kind === "road" || tile.kind === "mud" || tile.kind === "barricade") known += 1;
    else if (tile.kind === "tower_slot") known += 1;
  }
  return known;
}

/** Towers you have actually seen, for the summary line. */
export function knownTowers(view: WireFogView | null): { kind: string; count: number }[] {
  if (!view) return [];
  const counts = new Map<string, number>();
  for (const tile of view.tiles) {
    if (tile?.tower) counts.set(tile.tower.kind, (counts.get(tile.tower.kind) ?? 0) + 1);
  }
  return [...counts].map(([kind, count]) => ({ kind, count }));
}

/** The board index of a coordinate, for the mini map. */
export const tileAt = (view: WireFogView, x: number, y: number): WireFogView["tiles"][number] =>
  view.tiles[idx(x, y, BOARD_SIZE)] ?? null;
